"use client";

/**
 * solicitudesStore.ts
 * ────────────────────────────────────────────────────────────────
 * Store singleton (fuera de React) para la colección `solicitudes_polizas`.
 *
 * Antes, cada componente que llamaba a `useSolicitudes()` montaba su propio
 * `onSnapshot` sobre exactamente la misma query. Como `Sidebar` y
 * `GlobalSearch` viven en el layout (montados siempre) y encima la página
 * activa (dashboard, emisión, pre-clásicos, métricas, archivo, calendario)
 * también llama al hook, había entre 2 y 3 listeners idénticos activos en
 * todo momento — cada uno recibiendo y procesando las mismas ~300
 * actualizaciones.
 *
 * Este store mantiene UNA sola suscripción real a Firestore, contada por
 * referencias: se crea con el primer componente que se suscribe y se
 * destruye cuando el último se desmonta. Todos los consumidores comparten
 * el mismo snapshot en memoria vía `useSyncExternalStore`.
 * ────────────────────────────────────────────────────────────────
 */

import { collection, query, onSnapshot, orderBy, limit, Unsubscribe } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { SolicitudPolizaV3 } from "@/types/solicitud";

export const SOLICITUDES_LISTENER_LIMIT = 300;

interface StoreState {
  solicitudes: SolicitudPolizaV3[];
  loading: boolean;
  error: Error | null;
}

const SERVER_SNAPSHOT: StoreState = { solicitudes: [], loading: true, error: null };

let state: StoreState = SERVER_SNAPSHOT;
let unsubscribeFirestore: Unsubscribe | null = null;
let refCount = 0;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function setState(partial: Partial<StoreState>) {
  state = { ...state, ...partial };
  notify();
}

function startFirestoreListener() {
  const q = query(
    collection(db, "solicitudes_polizas"),
    orderBy("fechaCreacion", "desc"),
    limit(SOLICITUDES_LISTENER_LIMIT)
  );

  unsubscribeFirestore = onSnapshot(
    q,
    (snapshot) => {
      const data: SolicitudPolizaV3[] = [];
      snapshot.forEach((doc) => {
        data.push({ ...doc.data(), id: doc.id } as SolicitudPolizaV3);
      });
      setState({ solicitudes: data, loading: false, error: null });
    },
    (err) => {
      console.error("Error fetching solicitudes:", err);
      setState({ error: err, loading: false });
    }
  );
}

function stopFirestoreListener() {
  unsubscribeFirestore?.();
  unsubscribeFirestore = null;
  // Reset para que una nueva primera suscripción no muestre datos obsoletos
  // como si ya hubieran cargado.
  state = SERVER_SNAPSHOT;
}

export function subscribeSolicitudes(listener: () => void): () => void {
  listeners.add(listener);
  refCount++;
  if (refCount === 1) startFirestoreListener();

  return () => {
    listeners.delete(listener);
    refCount--;
    if (refCount === 0) stopFirestoreListener();
  };
}

export function getSolicitudesSnapshot(): StoreState {
  return state;
}

export function getSolicitudesServerSnapshot(): StoreState {
  return SERVER_SNAPSHOT;
}
